import { errorView, isRetryableError, type ErrorView } from '@krp/client-runtime'
import { priceKopecks, quantityNumber } from '@krp/sale-ui'

import { sameId } from '../../../shared/api/wire'
import type { AqsiDispatch } from '../../../shared/model/dispatch'
import { commandIntentOf } from '../../../shared/model/ids'
import { aqsiMySession } from '../../rmk/api/rmkApi'
import { aqsiSaleCancel, aqsiSaleCreate, aqsiSaleUpdateLines, type SaleLineRequest } from '../api/saleApi'
import type { SeatWithDevice } from './saleSlice'
import type { AqsiSale, CartDraftLine, CartSend, CartSendFailure } from './types'

/**
 * Очередь корзины (RP-SALE-FAST, шаг 3): чистые части отправки. Расписание, состояние и сами повторы — в слайсе `cartSlice` и в listener'е
 * `cartFlushRequested`; здесь — как замороженный запрос превращается в вызов службы и как отказ читается очередью.
 */

/** Позиции черновика → тело запроса службы: цену кабинета браузер не присылает, только цену кассира в копейках (RP-FB8). */
export function linesRequest(lines: readonly CartDraftLine[]): SaleLineRequest[] {
  return lines.map((line) => ({
    productResourceKey: line.productKey,
    quantity: quantityNumber(line.quantity),
    ...(line.barcode !== null ? { barcode: line.barcode } : {}),
    ...(line.price != null ? { manualPriceKopecks: priceKopecks(line.price) } : {}),
  }))
}

/** Контакт покупателя для POST/PUT: пустой — без контакта (служба заменяет его вместе с корзиной). */
export function contactRequest(contact: string | null | undefined): string | null {
  const text = contact?.trim() ?? ''
  return text === '' ? null : text
}

/** Что очередь делает с неудачной попыткой отправки. */
export type CartFailureClass =
  /** Исход неизвестен или служба недоступна: тот же запрос уходит ещё раз — служба узнает ключ и отдаст прежний результат. */
  | 'retry'
  /** Корзина изменилась в другом месте (409) или ответ не прочитан: правду скажет сервер, черновик снимается. */
  | 'server-truth'
  /** Служба отказала по существу (422, 403…): повтор вернёт тот же отказ, правку исправляет человек. */
  | 'refused'

export function classifyCartFailure(error: unknown): CartFailureClass {
  if (isRetryableError(error)) return 'retry'
  const kind = errorView(error).kind
  return kind === 'conflict' || kind === 'invalid-response' ? 'server-truth' : 'refused'
}

/** Отказ для хранилища и экрана: слова сервера дословно и номер запроса для администратора. */
export function failureOf(view: ErrorView): CartSendFailure {
  return {
    kind: view.kind,
    code: view.code,
    message: view.message,
    correlationId: view.correlationId,
    ...(view.status !== undefined ? { status: view.status } : {}),
  }
}

/**
 * Одна попытка замороженной отправки. Ключ и тело всегда те, что записаны в `send`: повтор не создаёт новой правки. Создание сначала
 * читает версию МОЕЙ сессии за кассой (договор C3b) и замораживает её (`onVersion`); `null` — работа за этой кассой не начата.
 * Отказ бросается как есть (`TransportError`), его читает `classifyCartFailure`.
 */
export async function sendCartRequest(
  dispatch: AqsiDispatch,
  ref: SeatWithDevice,
  send: CartSend,
  options: { forceSession: boolean; onVersion: (version: number) => void },
): Promise<AqsiSale | null> {
  const intent = commandIntentOf(send.operationId)
  if (send.kind === 'create') {
    let expectedVersion = send.expectedVersion
    if (expectedVersion === null) {
      // Сбой чтения (`unwrap` бросает его) — не «работа не начата»: его повторит очередь.
      const mine = await dispatch(
        aqsiMySession.initiate({ resourceId: ref.resourceId }, { subscribe: false, forceRefetch: options.forceSession }),
      ).unwrap()
      if (mine == null || !sameId(mine.registerKey, ref.registerKey)) return null
      expectedVersion = mine.version
      options.onVersion(expectedVersion)
    }
    if (send.paymentKind === null) throw new Error('Создание продажи без способа оплаты: очередь задаёт его при замораживании запроса')
    return dispatch(
      aqsiSaleCreate.initiate(
        {
          ...ref,
          intent,
          expectedVersion,
          paymentKind: send.paymentKind,
          lines: linesRequest(send.lines),
          customerContact: contactRequest(send.contact),
        },
        { track: false },
      ),
    ).unwrap()
  }
  if (send.saleId === null || send.expectedVersion === null) {
    throw new Error(`Отправка корзины «${send.kind}» без продажи или версии: очередь создаёт её только для существующей продажи`)
  }
  if (send.kind === 'cancel') {
    return dispatch(
      aqsiSaleCancel.initiate({ ...ref, saleId: send.saleId, intent, expectedVersion: send.expectedVersion }, { track: false }),
    ).unwrap()
  }
  return dispatch(
    aqsiSaleUpdateLines.initiate(
      {
        ...ref,
        saleId: send.saleId,
        intent,
        expectedVersion: send.expectedVersion,
        lines: linesRequest(send.lines),
        customerContact: contactRequest(send.contact),
        // Способ оплаты едет вместе с корзиной только когда кассир его сменил (служба 0.22.0).
        ...(send.paymentKind !== null ? { paymentKind: send.paymentKind } : {}),
      },
      { track: false },
    ),
  ).unwrap()
}
